import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { type PresentacionListItem } from '../../../catalogo/especies/types/especie';
import { EspecieService } from '../../../catalogo/especies/services/especieService';
import { ArticuloService } from '../../../catalogo/articulos/services/articuloService';
import { type ArticuloMateriaPrima } from '../types';

interface ProductInfo {
  idArticulo: number;
  codigoArticulo: string;
  descripcion: string;
  calibre: string;
  empaqueInterno: string;
  coeficiente: number;
  pesoEmpaqueInterior?: number;
}

interface ProductGridCell {
  calibre: string;
  empaqueInterno: string;
  cantidad: number;
  productos: ProductInfo[];
}

interface PresentacionSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectProducts: (selectedProducts: ArticuloMateriaPrima[]) => void;
  idEspecie: number;
  especieNombre?: string;
  editingProducts?: ArticuloMateriaPrima[];
}

const PresentacionSelectionModal: React.FC<PresentacionSelectionModalProps> = ({
  open,
  onClose,
  onSelectProducts,
  idEspecie,
  especieNombre = 'N/A',
  editingProducts,
}) => {
  const [presentaciones, setPresentaciones] = useState<PresentacionListItem[]>([]);
  const [selectedPresentacion, setSelectedPresentacion] = useState<number | null>(null);
  const [productos, setProductos] = useState<ProductInfo[]>([]);
  const [productGrid, setProductGrid] = useState<ProductGridCell[][]>([]);
  const [calibres, setCalibresList] = useState<string[]>([]);
  const [empaques, setEmpaquesList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPresentaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await EspecieService.getPresentacionesByEspecie(idEspecie);
      setPresentaciones(result);
    } catch (err) {
      setError('Error al cargar las presentaciones');
      console.error('Error loading presentaciones:', err);
    } finally {
      setLoading(false);
    }
  }, [idEspecie]);

  const loadProductsByPresentacion = useCallback(async (idPresentacion: number) => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Loading products for especie ${idEspecie} and presentacion ${idPresentacion}`);

      const result = await ArticuloService.getProductosByEspecieYPresentacion(idEspecie, idPresentacion);
      console.log('Products loaded from API:', result);
      console.log('Products structure check:', {
        isArray: Array.isArray(result),
        length: result?.length,
        firstProduct: result?.[0],
        firstProductKeys: result?.[0] ? Object.keys(result[0]) : [],
        hasCalibres: result?.some(p => p.calibre),
        hasEmpaques: result?.some(p => p.empaqueInterno),
        allCalibreFields: result?.map(p => ({ calibre: p.calibre, empaqueInterno: p.empaqueInterno }))
      });

      // Si el API retorna datos válidos, mapear campos si es necesario
      if (result && result.length > 0) {
        const mappedProducts = result.map(product => {
          // Log del producto original
          console.log('Original product:', product);

          // Mapear campos alternativos comunes
          const mapped = {
            ...product,
            // Mapeo de calibre (usar calibreNombre del API)
            calibre: product.calibreNombre ||
                    product.calibre ||
                    product.calibreDescripcion ||
                    product.calibre_descripcion ||
                    product.nombreCalibre ||
                    product.talla ||
                    product.size ||
                    'Sin Calibre',

            // Mapeo de empaque interno (usar empaqueInteriorNombre del API)
            empaqueInterno: product.empaqueInteriorNombre ||
                           product.empaqueInterno ||
                           product.empaqueInternoDescripcion ||
                           product.empaque_interno_descripcion ||
                           product.nombreEmpaqueInterno ||
                           product.empaque ||
                           product.packaging ||
                           'Sin Empaque',

            // Asegurar otros campos necesarios
            idArticulo: product.idArticulo || product.id || product.articuloId || 0,
            codigoArticulo: product.codigoArticulo || product.codigo || product.sku || 'N/A',
            descripcion: product.descripcion || product.nombre || product.description || 'Sin Descripción',
            coeficiente: product.coeficiente || product.peso || product.weight || 1,
            pesoEmpaqueInterior: product.pesoEmpaqueInterior || 0
          };

          console.log('Mapped product:', mapped);
          return mapped;
        });

        console.log('All mapped products:', mappedProducts);
        console.log('Unique calibres found:', [...new Set(mappedProducts.map(p => p.calibre))]);
        console.log('Unique empaques found:', [...new Set(mappedProducts.map(p => p.empaqueInterno))]);

        setProductos(mappedProducts);
      } else {
        // Datos de prueba mientras se implementa el endpoint
        console.log('No data from API or endpoint not found, using demo data');
        const demoProducts = [
          {
            idArticulo: 1001 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-001`,
            descripcion: `Producto Demo ${idPresentacion} - Grande 10kg`,
            calibre: 'Grande',
            empaqueInterno: 'Caja 10kg',
            coeficiente: 10.5
          },
          {
            idArticulo: 1002 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-002`,
            descripcion: `Producto Demo ${idPresentacion} - Grande 5kg`,
            calibre: 'Grande',
            empaqueInterno: 'Caja 5kg',
            coeficiente: 5.2
          },
          {
            idArticulo: 1003 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-003`,
            descripcion: `Producto Demo ${idPresentacion} - Mediano 10kg`,
            calibre: 'Mediano',
            empaqueInterno: 'Caja 10kg',
            coeficiente: 8.7
          },
          {
            idArticulo: 1004 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-004`,
            descripcion: `Producto Demo ${idPresentacion} - Mediano 5kg`,
            calibre: 'Mediano',
            empaqueInterno: 'Caja 5kg',
            coeficiente: 4.3
          },
          {
            idArticulo: 1005 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-005`,
            descripcion: `Producto Demo ${idPresentacion} - Pequeño 10kg`,
            calibre: 'Pequeño',
            empaqueInterno: 'Caja 10kg',
            coeficiente: 12.1
          },
          {
            idArticulo: 1006 + idPresentacion,
            codigoArticulo: `DEMO-${idEspecie}-${idPresentacion}-006`,
            descripcion: `Producto Demo ${idPresentacion} - Pequeño 2kg`,
            calibre: 'Pequeño',
            empaqueInterno: 'Bolsa 2kg',
            coeficiente: 2.8
          }
        ];
        setProductos(demoProducts);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      console.log('API error, using demo data as fallback');

      // Datos de respaldo en caso de error
      const fallbackProducts = [
        {
          idArticulo: 2001 + idPresentacion,
          codigoArticulo: `FALLBACK-${idEspecie}-${idPresentacion}-001`,
          descripcion: `Producto Fallback ${idPresentacion} - Extra Grande`,
          calibre: 'Extra Grande',
          empaqueInterno: 'Caja 15kg',
          coeficiente: 15.0
        },
        {
          idArticulo: 2002 + idPresentacion,
          codigoArticulo: `FALLBACK-${idEspecie}-${idPresentacion}-002`,
          descripcion: `Producto Fallback ${idPresentacion} - Estándar`,
          calibre: 'Estándar',
          empaqueInterno: 'Caja 8kg',
          coeficiente: 8.0
        }
      ];
      setProductos(fallbackProducts);
      setError(null); // Clear error since we have fallback data
    } finally {
      setLoading(false);
    }
  }, [idEspecie]);

  const buildProductGrid = useCallback(() => {
    console.log('Building product grid with productos:', productos);

    if (!productos || productos.length === 0) {
      console.log('No productos available for grid building');
      setProductGrid([]);
      setCalibresList([]);
      setEmpaquesList([]);
      return;
    }

    // Extract unique calibres and empaques with more validation
    const calibreValues = productos.map(p => p.calibre).filter(c => c && c !== 'undefined' && c !== 'null');
    const empaqueValues = productos.map(p => p.empaqueInterno).filter(e => e && e !== 'undefined' && e !== 'null');

    // Ordenar calibres por número (de menor a mayor) basado en el número antes del guión
    const uniqueCalibresList = [...new Set(calibreValues)].sort((a, b) => {
      // Extraer el número antes del guión de cada calibre
      const getNumberFromCalibre = (calibre: string): number => {
        const match = calibre.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      };

      const numA = getNumberFromCalibre(a);
      const numB = getNumberFromCalibre(b);

      // Ordenar de menor a mayor
      return numA - numB;
    });

    // Ordenar empaques: CAJAS primero, luego BOLSAS, luego SALDOS
    const uniqueEmpaquesList = [...new Set(empaqueValues)].sort((a, b) => {
      const getEmpaqueOrder = (empaque: string): number => {
        const empaqueUpper = empaque.toUpperCase();
        if (empaqueUpper.includes('CAJA')) return 1;
        if (empaqueUpper.includes('BOLSA')) return 2;
        if (empaqueUpper.includes('SALDO')) return 3;
        return 4; // Otros al final
      };
      const orderDiff = getEmpaqueOrder(a) - getEmpaqueOrder(b);
      // Si tienen el mismo orden de categoría, ordenar alfabéticamente
      return orderDiff !== 0 ? orderDiff : a.localeCompare(b);
    });

    console.log('Raw calibre values:', calibreValues);
    console.log('Raw empaque values:', empaqueValues);
    console.log('Extracted calibres:', uniqueCalibresList);
    console.log('Extracted empaques:', uniqueEmpaquesList);

    // Si no hay calibres o empaques válidos, no podemos construir la grilla
    if (uniqueCalibresList.length === 0 || uniqueEmpaquesList.length === 0) {
      console.warn('Cannot build grid: missing calibres or empaques');
      console.warn('Calibres available:', uniqueCalibresList);
      console.warn('Empaques available:', uniqueEmpaquesList);

      // Intentar crear una grilla básica con los productos disponibles
      if (productos.length > 0) {
        const fallbackGrid: ProductGridCell[][] = [[{
          calibre: 'Producto',
          empaqueInterno: 'Disponible',
          cantidad: 0,
          productos: productos,
        }]];
        setProductGrid(fallbackGrid);
        setCalibresList(['Producto']);
        setEmpaquesList(['Disponible']);
        console.log('Created fallback grid with all products in one cell');
      }
      return;
    }

    setCalibresList(uniqueCalibresList);
    setEmpaquesList(uniqueEmpaquesList);

    // Build the grid
    const grid: ProductGridCell[][] = [];
    for (let i = 0; i < uniqueCalibresList.length; i++) {
      const row: ProductGridCell[] = [];
      for (let j = 0; j < uniqueEmpaquesList.length; j++) {
        const calibre = uniqueCalibresList[i];
        const empaque = uniqueEmpaquesList[j];
        const matchingProducts = productos.filter(
          p => p.calibre === calibre && p.empaqueInterno === empaque
        );

        console.log(`Grid cell [${i}][${j}] - Calibre: "${calibre}", Empaque: "${empaque}", Products: ${matchingProducts.length}`);

        row.push({
          calibre,
          empaqueInterno: empaque,
          cantidad: 0,
          productos: matchingProducts,
        });
      }
      grid.push(row);
    }

    console.log('Built product grid:', grid);
    console.log('Grid dimensions:', { rows: grid.length, cols: grid[0]?.length || 0 });
    console.log('Total cells with products:', grid.flat().filter(cell => cell.productos.length > 0).length);
    setProductGrid(grid);
  }, [productos]);

  const handlePresentacionSelect = (idPresentacion: number) => {
    setSelectedPresentacion(idPresentacion);
  };

  const handleCantidadChange = (calibreIndex: number, empaqueIndex: number, newValue: string) => {
    const cell = productGrid[calibreIndex][empaqueIndex];
    const isSaldoColumn = cell.empaqueInterno.toLowerCase().includes('saldo');

    // Permitir campo vacío para que el usuario pueda borrar los ceros
    if (newValue === '' || newValue === '-') {
      setProductGrid(prev => {
        const newGrid = [...prev];
        newGrid[calibreIndex][empaqueIndex] = {
          ...newGrid[calibreIndex][empaqueIndex],
          cantidad: 0,
        };
        return newGrid;
      });
      return;
    }

    const cantidad = isSaldoColumn ? parseFloat(newValue) : parseInt(newValue);
    if (isNaN(cantidad)) return;

    setProductGrid(prev => {
      const newGrid = [...prev];
      newGrid[calibreIndex][empaqueIndex] = {
        ...newGrid[calibreIndex][empaqueIndex],
        cantidad: Math.max(0, cantidad),
      };
      return newGrid;
    });
  };

  const handleConfirmSelection = () => {
    // Collect all selected products with quantities > 0
    const selectedProducts: ArticuloMateriaPrima[] = [];

    // Obtener el nombre de la presentación seleccionada
    const presentacionSeleccionada = presentaciones.find(p => p.idPresentacion === selectedPresentacion);
    const nombrePresentacion = presentacionSeleccionada?.descripcion || 'Sin Presentación';

    productGrid.forEach(row => {
      row.forEach(cell => {
        if (cell.cantidad > 0 && cell.productos.length > 0) {
          // For each product in the cell, create an entry
          cell.productos.forEach(producto => {
            const pesoEmpaqueInterior = producto.pesoEmpaqueInterior || 0;

            // Construir una descripción única que incluya el empaque interno
            const descripcionCompleta = `${producto.descripcion} - ${cell.empaqueInterno}`;

            selectedProducts.push({
              id: Date.now() + Math.random(), // Generate unique ID
              idArticulo: producto.idArticulo,
              codigoArticulo: producto.codigoArticulo,
              descripcion: descripcionCompleta, // Incluir empaque en descripción
              especieNombreComun: especieNombre,
              coeficiente: producto.coeficiente,
              pesoEmpaqueInterior: pesoEmpaqueInterior,
              cantidad: cell.cantidad,
              total: pesoEmpaqueInterior * cell.cantidad,
              duenio: '',
              encargado: '',
              calibre: cell.calibre,
              presentacion: nombrePresentacion,
              idPresentacion: selectedPresentacion || undefined,
            });
          });
        }
      });
    });

    console.log('Confirmed selected products:', selectedProducts);
    onSelectProducts(selectedProducts);
    handleClose();
  };

  const handleClose = () => {
    setSelectedPresentacion(null);
    setProductos([]);
    setProductGrid([]);
    setCalibresList([]);
    setEmpaquesList([]);
    setError(null);
    onClose();
  };

  const getTotalSelectedProducts = () => {
    return productGrid.reduce((total, row) => {
      return total + row.reduce((rowTotal, cell) => {
        return rowTotal + (cell.cantidad > 0 ? cell.productos.length * cell.cantidad : 0);
      }, 0);
    }, 0);
  };

  const getTotalsByEmpaque = () => {
    const totals: { [empaque: string]: number } = {};

    // Inicializar totales para cada empaque
    empaques.forEach(empaque => {
      totals[empaque] = 0;
    });

    // Sumar cantidades multiplicadas por peso del empaque
    productGrid.forEach(row => {
      row.forEach(cell => {
        if (cell.cantidad > 0 && cell.productos.length > 0) {
          // Obtener el peso del empaque del primer producto (todos en la celda tienen el mismo empaque)
          const pesoEmpaque = cell.productos[0].pesoEmpaqueInterior || 1;

          // Multiplicar cantidad por peso del empaque
          const pesoTotal = cell.cantidad * pesoEmpaque;

          totals[cell.empaqueInterno] += pesoTotal;
        }
      });
    });

    return totals;
  };

  const getTotalsByCalibre = () => {
    const totals: { [calibre: string]: number } = {};

    // Inicializar totales para cada calibre
    calibres.forEach(calibre => {
      totals[calibre] = 0;
    });

    // Sumar cantidades multiplicadas por peso del empaque para cada calibre
    productGrid.forEach((row, calibreIndex) => {
      const calibre = calibres[calibreIndex];
      row.forEach(cell => {
        if (cell.cantidad > 0 && cell.productos.length > 0) {
          // Obtener el peso del empaque del primer producto
          const pesoEmpaque = cell.productos[0].pesoEmpaqueInterior || 1;

          // Multiplicar cantidad por peso del empaque
          const pesoTotal = cell.cantidad * pesoEmpaque;

          totals[calibre] += pesoTotal;
        }
      });
    });

    return totals;
  };

  // Load presentaciones when modal opens
  useEffect(() => {
    if (open && idEspecie) {
      loadPresentaciones();
    }
  }, [open, idEspecie, loadPresentaciones]);

  // Load products when presentacion is selected
  useEffect(() => {
    if (selectedPresentacion) {
      loadProductsByPresentacion(selectedPresentacion);
    }
  }, [selectedPresentacion, loadProductsByPresentacion]);

  // Build grid when productos change
  useEffect(() => {
    if (productos.length > 0) {
      buildProductGrid();
    }
  }, [productos, buildProductGrid]);

  // Auto-select presentation when editing
  useEffect(() => {
    if (editingProducts && editingProducts.length > 0 && open) {
      const editingPresentacionId = editingProducts[0]?.idPresentacion;
      console.log('Auto-selecting presentation for editing:', editingPresentacionId, editingProducts);

      if (editingPresentacionId && selectedPresentacion !== editingPresentacionId) {
        setSelectedPresentacion(editingPresentacionId);
      }
    }
  }, [editingProducts, open]);

  // Pre-populate grid with existing quantities when editing
  useEffect(() => {
    if (editingProducts && editingProducts.length > 0 && productGrid.length > 0 && empaques.length > 0 && calibres.length > 0) {
      console.log('Pre-populating grid with editing products:', editingProducts);
      console.log('Current grid state:', productGrid);
      console.log('Available calibres:', calibres);
      console.log('Available empaques:', empaques);

      // Create a map of existing quantities by caliber and empaque
      const quantityMap = new Map<string, number>();
      editingProducts.forEach(product => {
        console.log('Processing editing product:', {
          idArticulo: product.idArticulo,
          calibre: product.calibre,
          cantidad: product.cantidad,
          descripcion: product.descripcion
        });

        // Extraer el empaque interno de la descripción (formato: "descripción - empaque")
        const descripcionParts = product.descripcion.split(' - ');
        const empaqueFromDesc = descripcionParts.length > 1 ? descripcionParts[descripcionParts.length - 1] : null;

        console.log('Extracted empaque from description:', empaqueFromDesc);

        // Try to find matching cell by calibre and empaque
        productGrid.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            // Match by calibre AND empaque (extracted from description)
            const calibreMatch = cell.calibre === product.calibre;
            const empaqueMatch = empaqueFromDesc && cell.empaqueInterno === empaqueFromDesc;
            const articuloMatch = cell.productos.some(p => p.idArticulo === product.idArticulo);

            if (calibreMatch && (empaqueMatch || articuloMatch)) {
              const mapKey = `${cell.calibre}-${cell.empaqueInterno}`;
              console.log(`Found match for product ${product.idArticulo} at [${rowIndex}][${colIndex}]: ${mapKey}`);

              // If multiple products match the same cell, sum the quantities
              const currentQuantity = quantityMap.get(mapKey) || 0;
              quantityMap.set(mapKey, currentQuantity + product.cantidad);
            }
          });
        });
      });

      console.log('Quantity map for pre-population:', Object.fromEntries(quantityMap));

      // Update the grid with existing quantities
      if (quantityMap.size > 0) {
        setProductGrid(prev => {
          const newGrid = prev.map((row, calibreIndex) =>
            row.map((cell, empaqueIndex) => {
              const mapKey = `${cell.calibre}-${cell.empaqueInterno}`;
              const existingQuantity = quantityMap.get(mapKey);

              if (existingQuantity !== undefined) {
                console.log(`Setting quantity ${existingQuantity} for cell [${calibreIndex}][${empaqueIndex}] (${cell.calibre} - ${cell.empaqueInterno})`);
                return {
                  ...cell,
                  cantidad: existingQuantity,
                };
              }
              return cell;
            })
          );
          console.log('Updated grid:', newGrid);
          return newGrid;
        });
      } else {
        console.warn('No quantities matched - check if calibres and products align correctly');
        console.warn('editingProducts:', editingProducts);
        console.warn('productGrid sample:', productGrid[0]);
      }
    }
  }, [editingProducts, productGrid.length, empaques.length, calibres.length]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disablePortal
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6" component="div" fontWeight={600}>
            Seleccionar Productos por Presentación
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Especie: {especieNombre}
          </Typography>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          color="inherit"
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Left Panel - Presentaciones */}
        <Paper sx={{ width: 300, maxHeight: '100%', overflow: 'auto' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Presentaciones Disponibles
            </Typography>
          </Box>

          {loading && !selectedPresentacion && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          <List sx={{ p: 0 }}>
            {presentaciones.map((presentacion) => (
              <ListItem key={presentacion.idPresentacion} sx={{ p: 0 }}>
                <ListItemButton
                  selected={selectedPresentacion === presentacion.idPresentacion}
                  onClick={() => handlePresentacionSelect(presentacion.idPresentacion)}
                  sx={{
                    borderLeft: selectedPresentacion === presentacion.idPresentacion ? 3 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  <ListItemText
                    primary={presentacion.descripcion}
                    secondary={
                      <Chip
                        label={presentacion.esFresco ? 'Fresco' : 'Procesado'}
                        color={presentacion.esFresco ? 'success' : 'info'}
                        size="small"
                      />
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Right Panel - Products Grid */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!selectedPresentacion && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Seleccione una presentación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Elija una presentación de la lista para ver los productos disponibles
              </Typography>
            </Box>
          )}

          {selectedPresentacion && loading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          )}

          {selectedPresentacion && !loading && productGrid.length > 0 && (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Productos Disponibles - {presentaciones.find(p => p.idPresentacion === selectedPresentacion)?.descripcion}
                </Typography>
                {getTotalSelectedProducts() > 0 && (
                  <Chip
                    label={`${getTotalSelectedProducts()} productos seleccionados`}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Calibre</TableCell>
                      {empaques.map((empaque) => (
                        <TableCell key={empaque} align="center" sx={{ fontWeight: 600, minWidth: 150 }}>
                          {empaque}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light', minWidth: 120 }}>
                        Total por Calibre (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calibres.map((calibre, calibreIndex) => {
                      const totalsByCalibre = getTotalsByCalibre();
                      const totalCalibre = totalsByCalibre[calibre] || 0;

                      return (
                        <TableRow key={calibre} hover>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.50' }}>
                            {calibre}
                          </TableCell>
                          {empaques.map((empaque, empaqueIndex) => {
                            const cell = productGrid[calibreIndex][empaqueIndex];
                            const hasProducts = cell.productos.length > 0;

                            return (
                              <TableCell key={empaque} align="center">
                                {hasProducts ? (
                                  (() => {
                                    const isSaldoColumn = cell.empaqueInterno.toLowerCase().includes('saldo');
                                    return (
                                      <TextField
                                        size="small"
                                        value={cell.cantidad === 0 ? '' : cell.cantidad}
                                        onChange={(e) => handleCantidadChange(calibreIndex, empaqueIndex, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        placeholder="0"
                                        sx={{ width: 80 }}
                                        inputProps={{
                                          min: 0,
                                          type: 'number',
                                          step: isSaldoColumn ? 0.01 : 1,
                                          style: { textAlign: 'center' },
                                        }}
                                      />
                                    );
                                  })()
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light' }}>
                            {totalCalibre.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Fila de totales de peso por empaque */}
                    <TableRow sx={{ bgcolor: 'primary.light', '& td': { borderTop: 2, borderColor: 'primary.main' } }}>
                      <TableCell sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                        PESO TOTAL (kg)
                      </TableCell>
                      {empaques.map((empaque) => {
                        const totalsByEmpaque = getTotalsByEmpaque();
                        const pesoTotal = totalsByEmpaque[empaque] || 0;

                        return (
                          <TableCell key={empaque} align="center" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                            {pesoTotal.toFixed(2)}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          fontSize: '1.1rem',
                          border: 3,
                          borderColor: 'secondary.dark'
                        }}
                      >
                        {(() => {
                          const totalsByEmpaque = getTotalsByEmpaque();
                          const totalGeneral = Object.values(totalsByEmpaque).reduce((sum, peso) => sum + peso, 0);
                          return totalGeneral.toFixed(2);
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {selectedPresentacion && !loading && productos.length === 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay productos disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No se encontraron productos para esta presentación
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          color="primary"
          disabled={getTotalSelectedProducts() === 0}
          sx={{ fontWeight: 600 }}
        >
          Confirmar Selección ({getTotalSelectedProducts()} productos)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PresentacionSelectionModal;